/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { PlusOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentContainer } from '@/components/ContentContainer';
import { GuardedButton } from '@/components/GuardedButton';
import { PageHeader } from '@/components/PageHeader';
import { QueryWrapper } from '@/components/QueryWrapper';
import { usePaginatedM2MClientsQuery } from '@/hooks/query/usePaginatedM2MClientsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { DeleteM2MClientModal } from '@/pages/M2MClients/DeleteM2MClientModal';
import { M2MClientsTable } from '@/pages/M2MClients/M2MClientsTable';
import { Container } from '@/primitives/Container';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export function M2MClientsPage() {
  const { t } = useMimicTranslation('m2mClients');
  const navigate = useNavigate();
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const [clientToDelete, setClientToDelete] = useState<{ id: string; name: string } | undefined>(undefined);

  const m2mClientsQuery = usePaginatedM2MClientsQuery({
    type: 'queryParams',
  });

  if (!tenantID) return null;

  const deleteM2MClient = (clientId: string, clientName: string) => {
    setClientToDelete({ id: clientId, name: clientName });
  };

  const closeDeleteDialog = () => {
    setClientToDelete(undefined);
  };

  const navigateToCreateClient = () => {
    navigate(`/tenants/${tenantID}/m2m-clients/new`);
  };

  const primaryAction = (
    <GuardedButton
      requiredRole="superadmin"
      type="primary"
      dataTestId="create-m2m-client"
      onClick={navigateToCreateClient}
    >
      <PlusOutlined /> {t('newM2MClient')}
    </GuardedButton>
  );

  return (
    <ContentContainer width="100%">
      <Container full style={{ width: '100%' }}>
        <PageHeader title={t('listTitle')} level={2} />
        <QueryWrapper queryResult={m2mClientsQuery}>
          {(m2mClientsData) => (
            <M2MClientsTable
              m2mClientsData={m2mClientsData}
              updateQueryParams={m2mClientsQuery.updateQueryParams}
              m2mClientFilters={m2mClientsQuery.filters}
              deleteM2MClient={deleteM2MClient}
              primaryAction={primaryAction}
            />
          )}
        </QueryWrapper>

        {clientToDelete && (
          <DeleteM2MClientModal
            clientId={clientToDelete.id}
            clientName={clientToDelete.name}
            onCancelDeletion={closeDeleteDialog}
          />
        )}
      </Container>
    </ContentContainer>
  );
}
